import React, { useEffect, useState } from 'react';

// Graphene-style speedometer. `pct` is 0-100.
// The arc is 240° (30° to 330° going clockwise from bottom-left).
const START_DEG = 135; // top-left
const SWEEP_DEG = 270; // over the top
const SIZE = 280;
const STROKE = 22;

const polar = (cx, cy, r, angleDeg) => {
  const a = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const arcPath = (cx, cy, r, startAngle, endAngle) => {
  const s = polar(cx, cy, r, startAngle);
  const e = polar(cx, cy, r, endAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
};

const Speedometer = ({ pct = 0, label = 'CEILING', valueText = '' }) => {
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const target = Math.max(0, Math.min(100, Number(pct) || 0));
    const start = performance.now();
    const from = animPct;
    const dur = 900;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimPct(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pct]);

  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const r = (SIZE - STROKE) / 2;
  const endAngle = START_DEG + (SWEEP_DEG * animPct) / 100;
  const needleAngle = START_DEG + (SWEEP_DEG * animPct) / 100;
  const needle = polar(cx, cy, r - 8, needleAngle);

  // Tick marks around the arc
  const ticks = [];
  for (let i = 0; i <= 10; i++) {
    const t = START_DEG + (SWEEP_DEG * i) / 10;
    const inner = polar(cx, cy, r - STROKE / 2 - 8, t);
    const outer = polar(cx, cy, r - STROKE / 2 - 2, t);
    ticks.push(
      <line key={i} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
        stroke={i * 10 <= animPct ? 'var(--grx-neon)' : 'rgba(0,255,157,0.2)'}
        strokeWidth={i % 5 === 0 ? 2 : 1} />
    );
  }

  return (
    <div className="speedo">
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <defs>
          <linearGradient id="speedoGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"  stopColor="#00ff9d" />
            <stop offset="60%" stopColor="#00e5ff" />
            <stop offset="100%" stopColor="#7fff00" />
          </linearGradient>
          <filter id="speedoGlow"><feGaussianBlur stdDeviation="3" /></filter>
        </defs>
        {/* Background arc */}
        <path d={arcPath(cx, cy, r, START_DEG, START_DEG + SWEEP_DEG)}
              stroke="rgba(0, 255, 157, 0.08)" strokeWidth={STROKE} fill="none" strokeLinecap="round" />
        {/* Progress arc */}
        {animPct > 0 && (
          <>
            <path d={arcPath(cx, cy, r, START_DEG, endAngle)}
                  stroke="url(#speedoGrad)" strokeWidth={STROKE} fill="none" strokeLinecap="round"
                  filter="url(#speedoGlow)" opacity="0.5" />
            <path d={arcPath(cx, cy, r, START_DEG, endAngle)}
                  stroke="url(#speedoGrad)" strokeWidth={STROKE} fill="none" strokeLinecap="round" />
          </>
        )}
        {/* Tick marks */}
        {ticks}
        {/* Center hex + needle */}
        <polygon points={`${cx},${cy - 10} ${cx + 9},${cy - 5} ${cx + 9},${cy + 5} ${cx},${cy + 10} ${cx - 9},${cy + 5} ${cx - 9},${cy - 5}`}
          fill="var(--grx-neon)" filter="url(#speedoGlow)" opacity="0.8" />
        <line x1={cx} y1={cy} x2={needle.x} y2={needle.y}
          stroke="var(--grx-neon)" strokeWidth={3} strokeLinecap="round"
          filter="url(#speedoGlow)" style={{ transition: 'x2 0.6s, y2 0.6s' }} />
        <circle cx={cx} cy={cy} r="6" fill="var(--grx-black)" stroke="var(--grx-neon)" strokeWidth="2" />
      </svg>
      <div className="speedo-center">
        <div className="speedo-value">{animPct.toFixed(1)}%</div>
        <div className="speedo-label">{label}</div>
        {valueText && <div className="speedo-sub">{valueText}</div>}
      </div>
    </div>
  );
};

export default Speedometer;
